import { Platform } from 'react-native';
import Constants from 'expo-constants';

const isExpoDev =
  (typeof __DEV__ !== 'undefined' && __DEV__) || process.env.NODE_ENV === 'development';

/**
 * Public backend API base URL (no trailing slash).
 * Production: set EXPO_PUBLIC_API_URL (Vercel / EAS) or `expo.extra.apiUrl` in app.json
 * Local dev (web): empty string — webpack devServer proxies /api → backend (webpack.config.js).
 * Local dev (native): `localApiUrl` in app.json. EXPO_PUBLIC_API_URL is ignored in dev unless
 * EXPO_PUBLIC_FORCE_REMOTE_API=1.
 */
export const getApiUrl = () => {
  const extra = Constants?.expoConfig?.extra;
  const fromAppJson = (extra?.apiUrl && String(extra.apiUrl).trim()) || '';
  const localApiUrl = extra?.localApiUrl;
  const port = process.env.EXPO_PUBLIC_API_DEV_PORT || '5001';
  const forceRemote =
    (process.env.EXPO_PUBLIC_FORCE_REMOTE_API || '').trim() === '1';

  // Web (dev + production): same-origin /api via webpack (local) or Vercel rewrite (production)
  if (Platform.OS === 'web' && !forceRemote) {
    return '';
  }

  if (isExpoDev && !forceRemote) {
    if (localApiUrl) return localApiUrl;
  }

  const fromEnv = (process.env.EXPO_PUBLIC_API_URL || '').trim();
  if (fromEnv) return fromEnv;

  if (fromAppJson) return fromAppJson;

  if (Platform.OS === 'web' && (process.env.NODE_ENV !== 'production' || isExpoDev)) {
    return `http://localhost:${port}`;
  }

  if (isExpoDev && localApiUrl) return localApiUrl;

  // Production build with no config — will fail to connect until EXPO_PUBLIC_API_URL or extra.apiUrl is set
  if (!isExpoDev) {
    return 'https://api-url-not-configured.invalid';
  }

  return `http://localhost:${port}`;
};

const DEFAULT_PRODUCTION_API_URL = 'https://render-backend-vx0x.onrender.com';

/** Render (or configured) backend URL without trailing slash. */
export function getDirectApiUrl() {
  const extra = Constants?.expoConfig?.extra;
  const fromAppJson = (extra?.apiUrl && String(extra.apiUrl).trim()) || '';
  const fromEnv = (process.env.EXPO_PUBLIC_API_URL || '').trim();
  const url = fromEnv || fromAppJson;
  if (url) return url.replace(/\/$/, '');
  if (Platform.OS === 'web' && !isExpoDev) {
    return DEFAULT_PRODUCTION_API_URL;
  }
  return '';
}

/** Mobile browser user-agent (Android/iOS Chrome/Safari). */
function isMobileWebBrowser() {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
}

/**
 * API base for mobile web video uploads.
 * Same-origin /api (Vercel rewrite) — Android Chrome blocks cross-origin multipart file POSTs.
 */
export function getMobileWebUploadApiBase() {
  if (Platform.OS === 'web' && isMobileWebBrowser()) {
    return getApiUrl().replace(/\/$/, '');
  }
  return getYoutubeUploadJsonApiBase();
}

/** Build full API URL; empty base → relative same-origin path (e.g. /api/youtube/upload-file). */
export function buildApiUrl(apiBase, apiPath) {
  const path = apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
  const base = (apiBase || '').replace(/\/$/, '');
  return base ? `${base}${path}` : path;
}

/**
 * Same-origin /api base for YouTube upload-session calls (start, chunks, complete).
 * Web production uses Vercel rewrite; local web dev uses webpack proxy.
 */
export function getYoutubeUploadJsonApiBase() {
  const proxied = getApiUrl().replace(/\/$/, '');
  if (Platform.OS === 'web' && isExpoDev && !proxied) return '';
  if (proxied) return proxied;
  return getDirectApiUrl();
}

/**
 * API base for binary upload chunks.
 * Mobile web uses same-origin /api; desktop web production may use Render directly.
 * Local web dev uses same-origin /api (webpack proxy).
 */
export function getYoutubeUploadChunkApiBase() {
  if (Platform.OS === 'web' && isMobileWebBrowser()) {
    return getApiUrl().replace(/\/$/, '');
  }
  const proxied = getApiUrl().replace(/\/$/, '');
  if (Platform.OS === 'web') {
    if (isExpoDev && !proxied) return '';
    const direct = getDirectApiUrl();
    if (direct) return direct;
  }
  if (proxied) return proxied;
  return getDirectApiUrl();
}

/** @deprecated Use getYoutubeUploadJsonApiBase / getYoutubeUploadChunkApiBase */
export function getYoutubeUploadApiBase() {
  return getYoutubeUploadChunkApiBase();
}

/**
 * User-facing message when fetch() fails (backend down, wrong URL, CORS, etc.).
 * @param {unknown} error
 * @param {string} [apiUrl]
 */
export function formatApiFetchError(error, apiUrl = getApiUrl()) {
  const msg = error?.message || String(error);
  const refused =
    msg.includes('Failed to fetch') ||
    msg.includes('NetworkError') ||
    msg.includes('ERR_CONNECTION_REFUSED') ||
    msg.includes('Load failed');

  if (refused && Platform.OS === 'web' && isExpoDev) {
    const port = process.env.EXPO_PUBLIC_API_DEV_PORT || '5001';
    const via =
      apiUrl ||
      `this app’s /api proxy (webpack → 127.0.0.1:${port})`;
    return [
      `Cannot reach the API (${via}).`,
      '',
      'Local web dev needs the backend running:',
      '  Terminal 1: cd backend && npm install && npm run dev',
      '  Terminal 2: npm run web',
      '',
      'Or one command from the project root: npm run dev',
      '',
      `Check the backend terminal shows: Server is running on port ${port}`,
      'After pulling changes, restart npm run web so webpack picks up proxy config.',
    ].join('\n');
  }

  if (refused) {
    if (Platform.OS === 'web' && !isExpoDev) {
      return [
        'Cannot reach the API. The server may be waking up (first request after idle can take up to a minute).',
        'Please wait a moment and try again.',
      ].join(' ');
    }
    return `Cannot reach the API at ${apiUrl}. Start the backend (cd backend && npm run dev) and ensure app.json extra.localApiUrl matches your machine if using a phone simulator.`;
  }

  return msg || 'Network error';
}

export default getApiUrl;

/**
 * Retry fetch on transient network failures (e.g. after a long video upload on mobile).
 */
export async function fetchWithRetry(url, init = {}, { maxAttempts = 3, delayMs = 1000 } = {}) {
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await fetch(url, init);
    } catch (error) {
      lastError = error;
      const msg = error?.message || String(error);
      const retryable = /network|failed to fetch|load failed|request failed|abort/i.test(msg);
      if (!retryable || attempt === maxAttempts) {
        throw error;
      }
      await new Promise((r) => setTimeout(r, delayMs * attempt));
    }
  }
  throw lastError;
}
